import {
    IF,
    IS,
    nil,
    NO,
    UIButton,
    UIImageView,
    UIKeyValueSorter,
    UIKeyValueSorterSortingInstruction,
    LooseObject,
    UIObject,
    UITableView,
    UITextField,
    UITextView,
    UIView,
    YES
} from "uicore-ts"
import { CellView } from "../CellView"
import { RowView } from "../RowView"
import { CBCheckbox } from "./CBCheckbox"
import { CBColor } from "./CBColor"
import {
    CBDataViewCellDescriptor,
    CBDataViewColumnFilterCriterion,
    CBDataViewColumnFilterOption,
    CBSelectionColumnDescriptor
} from "./CBDataView"
import { CBTextField } from "./CBTextField"


const kUnsortedColumnImageAddress = "images/baseline-unfold_more-24px.svg"
const kDescendingColumnImageAddress = "images/baseline-expand_less-24px.svg"
const kAscendingColumnImageAddress = "images/baseline-expand_more-24px.svg"
const kFilterColumnImageAddress = "images/baseline-filter_list-24px.svg"

const kCompactHeaderHeight = 50
const kFilterButtonSize = 24
const kFilterPanelHeight = 44
const kHeaderControlGap = 4

type CBTableHeaderFilterControls = {
    fullIndex: number
    buttonView: UIView
    iconView: UIImageView
    panelView: UIView
    textField?: UIView
    minField?: UIView
    maxField?: UIView
    fromField?: UIView
    toField?: UIView
    categoryTextField?: UIView
    selectView?: UIView
    clearButton: UIButton
}


export class CBTableHeaderView<DataType extends object = object> extends RowView<CellView> {
    
    /**
     * Ordering states indexed by **full descriptor index** (including hidden
     * descriptors). Hidden descriptors retain their state here so that their
     * sortingInstructions callback is always called with the correct value.
     */
    orderingStates: string[] = []
    
    sideCellsWidth = this.core.paddingLength
    
    private _descriptors: CBDataViewCellDescriptor<DataType>[] = []
    
    /**
     * Maps visible cell index → full descriptor index.
     * Rebuilt whenever descriptors are set.
     */
    _visibleDescriptorIndices: number[] = []
    
    /**
     * Monotonically increasing counter. Incremented each time a sortable column
     * header is clicked. Used to break priority ties in sortingInstructions.
     */
    _activationCounter = 0
    
    /**
     * Maps full descriptor index → activation order value at the time the column
     * was last clicked. Higher value = more recently clicked.
     */
    _activationOrderByIndex: Map<number, number> = new Map()
    
    _keyboardFocusedCellIndex: number | undefined = undefined
    
    columnFilterCriteriaDidChange?: () => void
    filterPanelExpansionDidChange?: () => void

    private _expandedFilterDescriptorIndex?: number
    private _filterControlsByFullIndex: Map<number, CBTableHeaderFilterControls> = new Map()
    private _columnFilterCriteriaByFullIndex: Map<number, CBDataViewColumnFilterCriterion> = new Map()

    /**
     * Populated by CBDataView after descriptors are set.
     * Maps column index → { descriptor, _headerCheckbox? }
     * The _headerCheckbox is created lazily by CBDataView._wireHeaderCheckboxes.
     */
    selectionColumnIndices?: Map<number, CBSelectionColumnDescriptor<DataType> & { _headerCheckbox?: CBCheckbox }>
    
    /**
     * The subset of descriptors that are visible (not hidden).
     * This drives cell creation and layout. Parallel to this.cells.
     */
    get visibleDescriptors(): CBDataViewCellDescriptor<DataType>[] {
        return this._descriptors.filter(descriptor => !descriptor.hidden)
    }
    
    get columnFilterCriteria(): CBDataViewColumnFilterCriterion[] {
        return Array.from(this._columnFilterCriteriaByFullIndex.entries())
            .sort(([firstIndex], [secondIndex]) => firstIndex - secondIndex)
            .map(([, criterion]) => criterion)
    }

    constructor(elementID: string, descriptors: CBDataViewCellDescriptor<DataType>[] = []) {
        
        super(elementID)
        
        this.viewHTMLElement.setAttribute("role", "row")
        this.viewHTMLElement.setAttribute("aria-rowindex", "0")
        
        UIObject.configureWithObject(this.contentInsets, { left: this.sideCellsWidth, right: this.sideCellsWidth })
        
        this.descriptors = descriptors
        
    }
    
    
    updateContentForButtonStates() {
        
        this.cells.forEach((cell, visibleIndex) => {
            
            if (cell.isAButton) {
                const fullIndex = this._visibleDescriptorIndices[visibleIndex]
                cell.rightImageSource = CBTableHeaderView.imageSourceForOrderingState[this.orderingStates[fullIndex] || "unordered"]
            }
            
        })
        
    }
    
    
    orderingStateDidChange() {
    
    }
    
    
    get descriptors() {
        return this._descriptors
    }
    
    set descriptors(descriptors) {
        this._descriptors = descriptors
        this.updateContent()
    }
    
    
    private updateContent() {
        
        const cells: CellView[] = []
        const visibleDescriptorIndices: number[] = []
        this._filterControlsByFullIndex.clear()
        this._columnFilterCriteriaByFullIndex.clear()
        this._expandedFilterDescriptorIndex = undefined
        
        // Initialise ordering states for all descriptors (including hidden ones).
        this.orderingStates = this._descriptors.map(
            descriptor => descriptor.initialOrderingState || CBTableHeaderView.orderingState.unordered
        )
        
        this._descriptors.forEach((descriptor, fullIndex) => {
            
            if (descriptor.hidden) {
                return
            }
            
            const visibleIndex = cells.length
            visibleDescriptorIndices.push(fullIndex)
            
            const cell = new CellView()
            
            cell.viewHTMLElement.setAttribute("role", "columnheader")
            cell.viewHTMLElement.setAttribute("aria-colindex", String(visibleIndex))
            
            const isButton = IS(descriptor.allowSorting) && IS(descriptor.keyPath)
            cell.isAButton = isButton
            
            if (isButton) {
                // CellView already provides the shared horizontal cell inset used by body rows.
                cell.buttonView!.contentPadding = 0
                
                if (descriptor.title === "" + descriptor.title) {
                    cell.buttonView!.titleLabel.text = descriptor.title
                    cell.buttonView!.titleLabel.localizedTextObject = nil
                }
                else {
                    cell.buttonView!.titleLabel.localizedTextObject = IF(descriptor.title)(() => descriptor.title)()
                    cell.buttonView!.titleLabel.text = nil
                }
                
                if (descriptor.titleLabelAlignment) {
                    cell.buttonView!.titleLabel.textAlignment = descriptor.titleLabelAlignment
                }

                // A centre-aligned (or unspecified → default-centre) sortable header must
                // inset both sides for the sort caret so its title stays centred over the
                // column; left/right-aligned titles keep the single right inset.
                cell.centersTitleWithinRightImageInset =
                    !descriptor.titleLabelAlignment ||
                    descriptor.titleLabelAlignment === UITextView.textAlignment.center

                cell.buttonView!.colors.titleLabel = {
                    normal: CBColor.primaryContentColor,
                    highlighted: CBColor.primaryContentColor,
                    selected: CBColor.primaryContentColor
                }
                
                cell.buttonView!.titleLabel.configureWithObject({
                    style: { fontWeight: "bold", fontStyle: "initial" }
                })
                
                cell.buttonView!.updateContentForCurrentState()
                
                cell.rightImageSource = kUnsortedColumnImageAddress

                const activateOrdering = () => {
                    const currentState = this.orderingStates[fullIndex]
                    const nextState = CBTableHeaderView.nextStateForButtonState[currentState]

                    // Reset all visible columns to unordered, then set this one.
                    this._visibleDescriptorIndices.forEach(index => {
                        this.orderingStates[index] = CBTableHeaderView.orderingState.unordered
                    })
                    this.orderingStates[fullIndex] = nextState

                    // Record activation order for priority tiebreaking.
                    this._activationCounter++
                    this._activationOrderByIndex.set(fullIndex, this._activationCounter)

                    this.updateContentForButtonStates()
                    this.orderingStateDidChange()
                }

                cell.buttonView!.addTargetForControlEvents(
                    [UIButton.controlEvent.EnterDown, UIButton.controlEvent.PointerUpInside],
                    activateOrdering
                )

                // Right-aligned headers reserve a separate slot for the caret, so
                // the button itself does not extend underneath that icon. Make the
                // caret participate in the same action instead of leaving a dead
                // pointer area at the visually obvious click target.
                cell._rightImageView!.userInteractionEnabled = YES
                cell._rightImageView!.style.cursor = "pointer"
                cell._rightImageView!.addTargetForControlEvent(
                    UIView.controlEvent.PointerUpInside,
                    activateOrdering
                )
                
            }
            else {
                
                cell.initializeLabelVariant()
                
                if (descriptor.title === "" + descriptor.title) {
                    cell.titleLabel!.text = descriptor.title
                    cell.titleLabel!.localizedTextObject = nil
                }
                else {
                    cell.titleLabel!.localizedTextObject = IF(descriptor.title)(() => descriptor.title)()
                    cell.titleLabel!.text = nil
                }
                
                if (descriptor.titleLabelAlignment) {
                    cell.titleLabel!.textAlignment = descriptor.titleLabelAlignment
                }
                
                cell.titleLabel!.configureWithObject({
                    style: { fontWeight: "bold", fontStyle: "initial" }
                })
                
                cell.titleLabel!.textColor = CBColor.primaryContentColor
                
            }
            
            if (descriptor.filter) {
                const filterControls = this._createFilterControlsForDescriptor(descriptor, fullIndex)
                this._filterControlsByFullIndex.set(fullIndex, filterControls)
                cell.addSubview(filterControls.buttonView)
                cell.addSubview(filterControls.panelView)
            }

            cells.push(cell)
            
        })
        
        this._visibleDescriptorIndices = visibleDescriptorIndices
        this.cells = cells
        
        const allUnordered = this.orderingStates.allMatch(
            value => value == CBTableHeaderView.orderingState.unordered
        )
        
        if (allUnordered) {
            // Fall back to descending on the first visible column.
            const firstVisibleFullIndex = this._visibleDescriptorIndices.firstElement
            if (firstVisibleFullIndex !== undefined) {
                this.orderingStates[firstVisibleFullIndex] = CBTableHeaderView.orderingState.descending
            }
        }
        
        this.updateContentForButtonStates()
        
    }
    
    
    private _createFilterControlsForDescriptor(
        descriptor: CBDataViewCellDescriptor<DataType>,
        fullIndex: number
    ): CBTableHeaderFilterControls {
        const buttonView = new UIView(this.elementID + "FilterButton" + fullIndex).configuredWithObject({
            hoverText: "Filter",
            style: {
                cursor: "pointer",
                borderRadius: "4px",
                border: "1px solid transparent",
                boxSizing: "border-box",
                backgroundColor: "transparent"
            }
        })
        buttonView.viewHTMLElement.setAttribute("role", "button")
        buttonView.viewHTMLElement.setAttribute("aria-label", "Filter")

        const iconView = new UIImageView(this.elementID + "FilterIcon" + fullIndex).configuredWithObject({
            imageSource: kFilterColumnImageAddress,
            userInteractionEnabled: NO,
            style: {
                opacity: "0.68"
            }
        })
        buttonView.addSubview(iconView)

        const panelView = new UIView(this.elementID + "FilterPanel" + fullIndex).configuredWithObject({
            backgroundColor: CBColor.whiteColor,
            hidden: YES,
            style: {
                borderTop: "1px solid rgba(0, 0, 0, 0.12)",
                boxSizing: "border-box",
                overflow: "hidden"
            }
        })

        const clearButton = new UIButton(this.elementID + "ClearFilterButton" + fullIndex).configuredWithObject({
            hoverText: "Clear filter",
            style: {
                cursor: "pointer",
                borderRadius: "4px",
                border: "1px solid rgba(0, 0, 0, 0.16)",
                backgroundColor: "#ffffff",
                boxSizing: "border-box"
            }
        })
        clearButton.titleLabel.text = "×"
        clearButton.titleLabel.textAlignment = UITextView.textAlignment.center
        panelView.addSubview(clearButton)

        const controls: CBTableHeaderFilterControls = {
            fullIndex,
            buttonView,
            iconView,
            panelView,
            clearButton
        }

        const filterDescriptor = descriptor.filter!
        if (filterDescriptor.dataType === "numeric") {
            controls.minField = this._newFilterNumberField(
                this.elementID + "FilterMin" + fullIndex,
                filterDescriptor.placeholder ?? "Min"
            )
            controls.maxField = this._newFilterNumberField(
                this.elementID + "FilterMax" + fullIndex,
                "Max"
            )
            this._addFilterControlChangeHandler(controls.minField, () => {
                this._updateFilterCriterionFromControls(fullIndex)
            })
            this._addFilterControlChangeHandler(controls.maxField, () => {
                this._updateFilterCriterionFromControls(fullIndex)
            })
            panelView.addSubview(controls.minField)
            panelView.addSubview(controls.maxField)
        }
        else if (filterDescriptor.dataType === "date") {
            controls.fromField = this._newFilterDateField(
                this.elementID + "FilterFrom" + fullIndex,
                filterDescriptor.fromPlaceholder ?? "From"
            )
            controls.toField = this._newFilterDateField(
                this.elementID + "FilterTo" + fullIndex,
                filterDescriptor.toPlaceholder ?? "To"
            )
            this._addFilterControlChangeHandler(controls.fromField, () => {
                this._updateFilterCriterionFromControls(fullIndex)
            })
            this._addFilterControlChangeHandler(controls.toField, () => {
                this._updateFilterCriterionFromControls(fullIndex)
            })
            panelView.addSubview(controls.fromField)
            panelView.addSubview(controls.toField)
        }
        else if (filterDescriptor.dataType === "category") {
            const options = this._normalizedFilterOptions(filterDescriptor.options)
            if (options.length > 0) {
                controls.selectView = this._newFilterCategoryDropdown(
                    this.elementID + "FilterSelect" + fullIndex,
                    filterDescriptor.placeholder ?? "All",
                    options
                )
                this._addFilterCategoryDropdownChangeHandler(controls.selectView, () => {
                    this._updateFilterCriterionFromControls(fullIndex)
                })
                panelView.addSubview(controls.selectView)
            }
            else {
                controls.categoryTextField = this._newFilterTextField(
                    this.elementID + "FilterCategory" + fullIndex,
                    filterDescriptor.placeholder ?? "Value",
                    undefined,
                    descriptor
                )
                this._addFilterControlChangeHandler(controls.categoryTextField, () => {
                    this._updateFilterCriterionFromControls(fullIndex)
                })
                panelView.addSubview(controls.categoryTextField)
            }
        }
        else {
            controls.textField = this._newFilterTextField(
                this.elementID + "FilterText" + fullIndex,
                filterDescriptor.placeholder ?? "Contains",
                undefined,
                descriptor
            )
            this._addFilterControlChangeHandler(controls.textField, () => {
                this._updateFilterCriterionFromControls(fullIndex)
            })
            panelView.addSubview(controls.textField)
        }

        buttonView.addTargetForControlEvent(UIView.controlEvent.PointerUpInside, (_sender, event) => {
            event?.stopPropagation?.()
            this._toggleFilterPanel(fullIndex)
        })
        buttonView.addTargetForControlEvent(UIView.controlEvent.EnterDown, (_sender, event) => {
            event?.stopPropagation?.()
            this._toggleFilterPanel(fullIndex)
        })
        clearButton.addTargetForControlEvents(
            [UIButton.controlEvent.EnterDown, UIButton.controlEvent.PointerUpInside],
            (_sender, event) => {
                event?.stopPropagation?.()
                this._clearFilterControls(fullIndex)
            }
        )

        return controls
    }


    protected _newFilterTextField(
        elementID: string,
        placeholderText: string,
        inputType?: string,
        _descriptor?: CBDataViewCellDescriptor<DataType>
    ): UIView {
        const textField = new CBTextField(elementID).configuredWithObject({
            style: {
                font: "inherit",
                fontSize: "12px",
                border: "1px solid rgba(0, 0, 0, 0.18)",
                borderRadius: "4px",
                padding: "0",
                boxSizing: "border-box",
                backgroundColor: "#ffffff",
                outline: "none",
                overflow: "hidden"
            }
        })
        textField.placeholderText = placeholderText
        textField.titleLabel.hidden = YES
        textField.titleLabel.style.display = "none"
        textField.textField.configureWithObject({
            placeholderText,
            style: {
                width: "100%",
                height: "100%",
                font: "inherit",
                fontSize: "12px",
                backgroundColor: "transparent",
                border: "none",
                outline: "none"
            },
            textElementView: {
                style: {
                    width: "100%",
                    height: "100%",
                    padding: "4px 6px",
                    border: "none",
                    outline: "none",
                    backgroundColor: "transparent",
                    boxSizing: "border-box",
                    font: "inherit",
                    fontSize: "12px"
                }
            }
        })
        if (inputType) {
            textField.textField.textElementView.viewHTMLElement.setAttribute("type", inputType)
        }
        return textField
    }


    protected _newFilterNumberField(elementID: string, placeholderText: string): UIView {
        return this._newFilterTextField(elementID, placeholderText)
    }


    protected _newFilterDateField(elementID: string, placeholderText: string): UIView {
        return this._newFilterTextField(elementID, placeholderText, "date")
    }


    protected _newFilterCategoryDropdown(
        elementID: string,
        placeholderText: string,
        options: { value: string; label: string }[]
    ): UIView {
        const selectElement = document.createElement("select")
        const placeholderElement = document.createElement("option")
        placeholderElement.value = ""
        placeholderElement.text = placeholderText
        selectElement.appendChild(placeholderElement)
        options.forEach(option => {
            const optionElement = document.createElement("option")
            optionElement.value = option.value
            optionElement.text = option.label
            selectElement.appendChild(optionElement)
        })
        return new UIView(
            elementID,
            selectElement as HTMLElement & LooseObject
        ).configuredWithObject({
            style: {
                font: "inherit",
                fontSize: "12px",
                border: "1px solid rgba(0, 0, 0, 0.18)",
                borderRadius: "4px",
                padding: "2px 6px",
                backgroundColor: "#ffffff",
                boxSizing: "border-box",
                outline: "none",
                width: "100%"
            }
        })
    }


    protected _addFilterControlChangeHandler(control: UIView, handler: () => void) {
        if (control instanceof CBTextField) {
            control.textField.addTargetForControlEvent(UITextField.controlEvent.TextChange, handler)
            return
        }
        control.addTargetForControlEvent(UITextField.controlEvent.TextChange, handler)
    }


    protected _addFilterCategoryDropdownChangeHandler(control: UIView, handler: () => void) {
        control.viewHTMLElement.addEventListener("change", handler)
    }


    protected _filterControlText(control: UIView | undefined): string {
        if (!control) {
            return ""
        }
        if (control instanceof CBTextField) {
            return control.text
        }
        if ("text" in control && typeof control.text === "string") {
            return control.text
        }
        return ""
    }


    protected _setFilterControlText(control: UIView | undefined, value: string) {
        if (!control) {
            return
        }
        if (control instanceof CBTextField) {
            control.text = value
            return
        }
        if ("text" in control) {
            Reflect.set(control, "text", value)
        }
    }


    protected _clearFilterControl(control: UIView | undefined) {
        this._setFilterControlText(control, "")
    }


    protected _clearFilterCategoryDropdown(control: UIView | undefined) {
        if (!control) {
            return
        }
        const selectElement = control.viewHTMLElement as HTMLSelectElement
        selectElement.selectedIndex = 0
    }


    protected _focusFilterControl(control: UIView) {
        control.focus()
    }


    protected _layoutFilterControl(control: UIView | undefined) {
        if (!control) {
            return
        }
        if (control instanceof CBTextField) {
            control.textField.frame = control.bounds
            control.titleLabel.frame = control.bounds
        }
        control.layoutIfNeeded()
    }


    protected _selectedValuesFromCategoryDropdown(selectView: UIView | undefined): string[] {
        if (!selectView) {
            return []
        }
        const selectElement = selectView.viewHTMLElement as HTMLSelectElement
        return Array.from(selectElement.selectedOptions)
            .map(option => option.value)
            .filter(value => value.length > 0)
    }


    private _normalizedFilterOptions(
        options: CBDataViewColumnFilterOption[] | undefined
    ): { value: string; label: string }[] {
        return (options ?? []).map(option => {
            if (typeof option === "object") {
                return {
                    value: option.value.toString(),
                    label: option.label
                }
            }
            return {
                value: option.toString(),
                label: option.toString()
            }
        })
    }


    private _toggleFilterPanel(fullIndex: number) {
        this._expandedFilterDescriptorIndex =
            this._expandedFilterDescriptorIndex === fullIndex ? undefined : fullIndex
        this._syncFilterPanelVisibility()
        this.filterPanelExpansionDidChange?.()
        this.setNeedsLayoutUpToRootView()

        const controls = this._filterControlsByFullIndex.get(fullIndex)
        if (this._expandedFilterDescriptorIndex === fullIndex && controls) {
            requestAnimationFrame(() => {
                const focusTarget = controls.textField ??
                    controls.minField ??
                    controls.fromField ??
                    controls.categoryTextField ??
                    controls.selectView
                if (focusTarget) {
                    this._focusFilterControl(focusTarget)
                }
            })
        }
    }


    private _syncFilterPanelVisibility() {
        this._filterControlsByFullIndex.forEach((controls, fullIndex) => {
            controls.panelView.hidden = this._expandedFilterDescriptorIndex !== fullIndex
            this._updateFilterButtonState(fullIndex)
        })
    }


    private _filterPanelHeightForDescriptor(descriptor: CBDataViewCellDescriptor<DataType>): number {
        return kFilterPanelHeight
    }


    private _activeFilterPanelHeight(): number {
        if (this._expandedFilterDescriptorIndex === undefined) {
            return 0
        }
        const descriptor = this._descriptors[this._expandedFilterDescriptorIndex]
        if (!descriptor?.filter) {
            return 0
        }
        return this._filterPanelHeightForDescriptor(descriptor)
    }


    private _criterionTitleForDescriptor(descriptor: CBDataViewCellDescriptor<DataType>): string | undefined {
        return typeof descriptor.title === "string" ? descriptor.title : undefined
    }


    private _criterionKeyPathForDescriptor(descriptor: CBDataViewCellDescriptor<DataType>): string {
        return (
            descriptor.filter?.backendKeyPath ??
            descriptor.filter?.keyPath ??
            descriptor.keyPath ??
            ""
        ).toString()
    }


    private _parseFilterNumber(value: string): number | undefined {
        const parsedValue = parseFloat(value.trim().replace(",", "."))
        return Number.isFinite(parsedValue) ? parsedValue : undefined
    }


    private _updateFilterCriterionFromControls(fullIndex: number) {
        const descriptor = this._descriptors[fullIndex]
        const filterDescriptor = descriptor?.filter
        const controls = this._filterControlsByFullIndex.get(fullIndex)
        if (!descriptor || !filterDescriptor || !controls) {
            return
        }

        let criterion: CBDataViewColumnFilterCriterion | undefined
        const sharedCriterionData = {
            columnIndex: fullIndex,
            title: this._criterionTitleForDescriptor(descriptor),
            keyPath: this._criterionKeyPathForDescriptor(descriptor),
            dataType: filterDescriptor.dataType
        }

        if (filterDescriptor.dataType === "numeric") {
            const min = this._parseFilterNumber(this._filterControlText(controls.minField))
            const max = this._parseFilterNumber(this._filterControlText(controls.maxField))
            if (min !== undefined || max !== undefined) {
                criterion = {
                    ...sharedCriterionData,
                    operator: "range",
                    min,
                    max
                }
            }
        }
        else if (filterDescriptor.dataType === "date") {
            const from = this._filterControlText(controls.fromField).trim()
            const to = this._filterControlText(controls.toField).trim()
            if (from.length > 0 || to.length > 0) {
                criterion = {
                    ...sharedCriterionData,
                    operator: "dateRange",
                    from,
                    to
                }
            }
        }
        else if (filterDescriptor.dataType === "category") {
            const selectedValues = controls.selectView
                                   ? this._selectedValuesFromCategoryDropdown(controls.selectView)
                                   : this._filterControlText(controls.categoryTextField)
                                       .split(",")
                                       .map(value => value.trim())
                                       .filter(value => value.length > 0)
            if (selectedValues.length > 0) {
                criterion = {
                    ...sharedCriterionData,
                    operator: "in",
                    values: selectedValues
                }
            }
        }
        else {
            const value = this._filterControlText(controls.textField).trim()
            if (value.length > 0) {
                criterion = {
                    ...sharedCriterionData,
                    operator: "contains",
                    value
                }
            }
        }

        if (criterion) {
            this._columnFilterCriteriaByFullIndex.set(fullIndex, criterion)
        }
        else {
            this._columnFilterCriteriaByFullIndex.delete(fullIndex)
        }

        this._updateFilterButtonState(fullIndex)
        this.columnFilterCriteriaDidChange?.()
    }


    private _clearFilterControls(fullIndex: number) {
        const controls = this._filterControlsByFullIndex.get(fullIndex)
        if (!controls) {
            return
        }
        this._clearFilterControl(controls.textField)
        this._clearFilterControl(controls.minField)
        this._clearFilterControl(controls.maxField)
        this._clearFilterControl(controls.fromField)
        this._clearFilterControl(controls.toField)
        this._clearFilterControl(controls.categoryTextField)
        this._clearFilterCategoryDropdown(controls.selectView)
        this._columnFilterCriteriaByFullIndex.delete(fullIndex)
        this._updateFilterButtonState(fullIndex)
        this.columnFilterCriteriaDidChange?.()
    }


    private _updateFilterButtonState(fullIndex: number) {
        const controls = this._filterControlsByFullIndex.get(fullIndex)
        if (!controls) {
            return
        }
        const isActive = this._columnFilterCriteriaByFullIndex.has(fullIndex)
        const isExpanded = this._expandedFilterDescriptorIndex === fullIndex
        controls.buttonView.configureWithObject({
            style: {
                backgroundColor: isActive || isExpanded ? "rgba(0, 101, 178, 0.12)" : "transparent",
                borderColor: isActive || isExpanded ? "rgba(0, 101, 178, 0.35)" : "transparent"
            }
        })
        controls.iconView.configureWithObject({
            style: {
                opacity: isActive || isExpanded ? "0.94" : "0.68"
            }
        })
    }


    private _layoutFilterControlsInsidePanel(controls: CBTableHeaderFilterControls) {
        const panelBounds = controls.panelView.contentBoundsWithInset(4)
        const clearButtonWidth = 28

        const firstRangeField = controls.minField ?? controls.fromField
        const secondRangeField = controls.maxField ?? controls.toField

        if (firstRangeField && secondRangeField) {
            panelBounds.distributeViewsAlongWidth(
                [firstRangeField, secondRangeField, controls.clearButton],
                [1, 1, 0],
                kHeaderControlGap,
                [nil, nil, clearButtonWidth]
            )
            this._layoutFilterControl(firstRangeField)
            this._layoutFilterControl(secondRangeField)
            this._layoutFilterControl(controls.clearButton)
            return
        }

        const primaryControl = controls.textField ??
            controls.categoryTextField ??
            controls.selectView

        if (!primaryControl) {
            controls.clearButton.frame = panelBounds.rectangleWithWidth(clearButtonWidth, 1)
            this._layoutFilterControl(controls.clearButton)
            return
        }

        panelBounds.distributeViewsAlongWidth(
            [primaryControl, controls.clearButton],
            [1, 0],
            kHeaderControlGap,
            [nil, clearButtonWidth]
        )
        this._layoutFilterControl(primaryControl)
        this._layoutFilterControl(controls.clearButton)
    }


    sortingInstructionsForCurrentState(): UIKeyValueSorterSortingInstruction[] {
        
        type InstructionBucket = {
            instructions: UIKeyValueSorterSortingInstruction[];
            priority: number;
            activationOrder: number;
        }
        
        const buckets: InstructionBucket[] = []
        
        this._descriptors.forEach((descriptor, fullIndex) => {
            
            const ownState = this.orderingStates[fullIndex] ?? CBTableHeaderView.orderingState.unordered
            
            let instructions: UIKeyValueSorterSortingInstruction[]
            
            if (descriptor.sortingInstructions) {
                instructions = descriptor.sortingInstructions(ownState, this.orderingStates)
            }
            else if (ownState !== CBTableHeaderView.orderingState.unordered) {
                instructions = [{
                    keyPath: descriptor.keyPath ?? "",
                    dataType: descriptor.dataType ?? UIKeyValueSorter.dataType.string,
                    direction: ownState
                }]
            }
            else {
                instructions = []
            }
            
            if (instructions.length === 0) {
                return
            }
            
            buckets.push({
                instructions,
                priority: descriptor.sortPriority ?? 0,
                activationOrder: this._activationOrderByIndex.get(fullIndex) ?? 0
            })
            
        })
        
        // Sort by priority descending (higher = first), then activation order
        // descending (more recently clicked = first) as the tiebreaker.
        buckets.sort((first, second) => {
            const priorityDelta = second.priority - first.priority
            if (priorityDelta !== 0) {
                return priorityDelta
            }
            return second.activationOrder - first.activationOrder
        })
        
        return buckets.flatMap(bucket => bucket.instructions)
        
    }
    
    get sortingInstructions() {
        return this.sortingInstructionsForCurrentState()
    }
    
    
    setKeyboardFocusedCellIndex(index: number | undefined) {
        
        this._keyboardFocusedCellIndex = index
        
        this.cells.forEach((cell, cellIndex) => {
            if (cell.variant == "button") {
                cell.buttonView!.highlighted = index !== undefined && cellIndex === index
            }
        })
        
    }
    
    
    activateFocusedCell() {
        
        if (this._keyboardFocusedCellIndex === undefined) {
            return
        }
        
        const cell = this.cells[this._keyboardFocusedCellIndex]
        if (cell?.isAButton) {
            cell.buttonView?.sendControlEventForKey(UIButton.controlEvent.PointerUpInside, nil)
        }
        
    }
    
    
    static orderingState: Record<string, string> = {
        "unordered": "unordered",
        "descending": UIKeyValueSorter.direction.descending,
        "ascending": UIKeyValueSorter.direction.ascending
    }
    
    static imageSourceForOrderingState: Record<string, string> = {
        "unordered": kUnsortedColumnImageAddress,
        [UIKeyValueSorter.direction.descending]: kDescendingColumnImageAddress,
        [UIKeyValueSorter.direction.ascending]: kAscendingColumnImageAddress
    }
    
    static nextStateForButtonState: Record<string, string> = {
        "unordered": UIKeyValueSorter.direction.descending,
        [UIKeyValueSorter.direction.descending]: UIKeyValueSorter.direction.ascending,
        [UIKeyValueSorter.direction.ascending]: UIKeyValueSorter.direction.descending
    }
    
    
    override layoutSubviews() {
        
        // Use cellWeights from superview (UITableView) if available
        const tableView = this.superview
        if (tableView instanceof UITableView && tableView.cellWeights) {
            this.cellWeights = tableView.cellWeights
        }
        
        super.layoutSubviews()
        
        const compactHeaderHeight = this._compactHeaderHeight()
        const headerIsExpanded = this._activeFilterPanelHeight() > 0
        this.cells.forEach((cell, visibleIndex) => {
            const fullIndex = this._visibleDescriptorIndices[visibleIndex]
            const controls = this._filterControlsByFullIndex.get(fullIndex)

            let rightInset = 0
            const compactRowFrame = cell.bounds.rectangleWithHeight(compactHeaderHeight)
            const titleRowFrame = compactRowFrame.rectangleWithInsets(
                cell.contentInsets.left,
                cell.contentInsets.right,
                cell.contentInsets.bottom,
                cell.contentInsets.top
            )

            if (controls) {
                controls.buttonView.frame = titleRowFrame
                    .rectangleWithWidth(kFilterButtonSize, 1)
                    .rectangleByAddingX(-rightInset)
                    .rectangleWithHeight(kFilterButtonSize, 0.5)
                controls.iconView.frame = controls.buttonView.bounds
                    .rectangleWithWidth(18, 0.5)
                    .rectangleWithHeight(18, 0.5)
                controls.buttonView.moveToTopOfSuperview()
                rightInset += kFilterButtonSize + kHeaderControlGap

                const descriptor = this._descriptors[fullIndex]
                controls.panelView.hidden = this._expandedFilterDescriptorIndex !== fullIndex
                if (!controls.panelView.hidden && descriptor) {
                    controls.panelView.frame = cell.bounds
                        .rectangleWithY(compactHeaderHeight)
                        .rectangleWithHeight(this._filterPanelHeightForDescriptor(descriptor))
                    this._layoutFilterControlsInsidePanel(controls)
                    controls.panelView.moveToTopOfSuperview()
                }
            }

            const activeView = cell.buttonView ?? cell.titleLabel
            if (activeView && (rightInset > 0 || headerIsExpanded)) {
                const horizontalInset = Math.ceil(rightInset)
                activeView.frame = titleRowFrame.rectangleWithInsets(
                    cell.centersTitleWithinRightImageInset ? horizontalInset : 0,
                    horizontalInset,
                    0,
                    0
                )
                activeView.layoutIfNeeded()
            }
        })

        // Position header checkboxes over their corresponding column cells.
        if (this.selectionColumnIndices) {
            for (const [columnIndex, entry] of this.selectionColumnIndices) {
                const checkbox = entry._headerCheckbox
                if (!checkbox) {
                    continue
                }
                const cell = this.cells[columnIndex]
                if (!cell) {
                    continue
                }
                // When the descriptor has a title the checkbox must be wide enough to
                // show its label alongside the indicator. Use the full cell width.
                // When there is no title a square (height × height) centred in the cell
                // is sufficient and keeps the column compact.
                const hasTitle = !!checkbox.titleLabel.text
                const titleRowFrame = cell.frame.rectangleWithHeight(compactHeaderHeight)
                checkbox.frame = IF(hasTitle)(
                    () => titleRowFrame
                ).ELSE(
                    () => titleRowFrame
                        .rectangleWithWidth(compactHeaderHeight, 0.5)
                        .rectangleWithHeight(compactHeaderHeight, 0.5)
                )
                checkbox.moveToTopOfSuperview()
            }
        }
        
    }
    
    
    override intrinsicContentHeight(constrainingWidth?: number): number {
        return [
            kCompactHeaderHeight,
            super.intrinsicContentHeight(constrainingWidth)
        ].max() + this._activeFilterPanelHeight()
    }

    private _compactHeaderHeight(): number {
        return [
            kCompactHeaderHeight,
            this.bounds.height - this._activeFilterPanelHeight()
        ].max()
    }


}
